import { uniqBy } from 'es-toolkit';
import React from 'react';
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, TextField, Tooltip, Typography } from '@mui/material';

import { formatLengthToArchitecturalString, PropertySelectField } from '@paragon/client-tools';
import { Bearing, ComponentDesign, JackGirderConnection, MemberType } from '@paragon/contract-library';
import { Direction2D, Line2D, Point2D } from '@paragon/geometry-library';
import { connectorDisplayName } from '@paragon/layout-models';
import { AngleField, DistanceField, NumericField } from '@paragon/material';
import { calculateMemberPolygon } from '@paragon/three-shared';
import { humanCase, notNullish, throwIfNull } from '@paragon/utilities';

import { selectItem } from '../../../../store/actions/selectActions';
import { getBearingVisualData } from '../../../../store/selectors/bearingGeometrySelectors';
import { useAppDispatch } from '../../../hooks';
import { PropertiesPanelSubtitle } from './PropertiesPanelSubtitle';

const memberTypeOptions: readonly MemberType[] = ['TopChord', 'BottomChord'];
const chordToUseWhenMultipleChoicesOptions: ('Outer' | 'Inner')[] = ['Outer', 'Inner'];

const minimumAngleBetweenGirderAndCarriedComponentLowerBound = 0.01;
const minimumAngleBetweenGirderAndCarriedComponentUpperBound = 90;
const minimumNumberOfJacks = 1;

export function getDefaultCarriedComponentConnection(carryingComponentDesignGuid: string): JackGirderConnection {
  return {
    guid: crypto.randomUUID(),
    locked: true,
    userCreated: true,
    girderGuid: carryingComponentDesignGuid,
    jackGuid: '',
    jackBearingGuid: '',
    numberOfJacks: minimumNumberOfJacks,
    spacingOfJacks: 24,
    memberType: 'BottomChord',
    chordToUseWhenMultipleChoices: 'Outer',
    xLocation: 0,
    minimumAngleBetweenGirderAndJack: minimumAngleBetweenGirderAndCarriedComponentUpperBound,
    skewFromLeft: null,
    girderFace: null,
    hangerGuid: null,
    hangerCarriedItemConfigurationGuid: null,
  };
}

export function carriedComponentConnectionIsValid(
  carriedComponentConnection: JackGirderConnection,
  carryingComponentDesign: ComponentDesign,
  carriedComponentDesignCandidates: readonly ComponentDesign[],
) {
  const selectedCarriedComponentDesign = getSelectedCarriedComponentDesign(
    carriedComponentConnection,
    carriedComponentDesignCandidates,
  );
  const bearingsOnCarriedComponentDesign = selectedCarriedComponentDesign?.component.bearings ?? [];
  const selectedBearingOnCarriedComponentDesign = getSelectedBearingOnCarriedComponentDesign(
    carriedComponentConnection,
    bearingsOnCarriedComponentDesign,
  );

  const invalid =
    !selectedBearingOnCarriedComponentDesign ||
    carriedComponentConnection.minimumAngleBetweenGirderAndJack <
      minimumAngleBetweenGirderAndCarriedComponentLowerBound ||
    carriedComponentConnection.minimumAngleBetweenGirderAndJack >
      minimumAngleBetweenGirderAndCarriedComponentUpperBound ||
    !xLocationIsValid(
      carriedComponentConnection.xLocation,
      carriedComponentConnection.memberType,
      carryingComponentDesign,
    ) ||
    getFirstInvalidSpacedCarriedComponent(
      carriedComponentConnection.xLocation,
      carriedComponentConnection.memberType,
      carriedComponentConnection.numberOfJacks,
      carriedComponentConnection.spacingOfJacks,
      carryingComponentDesign,
    ) !== null ||
    carriedComponentConnection.numberOfJacks < minimumNumberOfJacks ||
    !Number.isInteger(carriedComponentConnection.numberOfJacks);

  return !invalid;
}

export function getSelectedCarriedComponentDesign(
  carriedComponentConnection: JackGirderConnection,
  carriedComponentDesignCandidates: readonly ComponentDesign[],
) {
  return carriedComponentDesignCandidates.find((c) => c.guid === carriedComponentConnection.jackGuid) ?? null;
}

export function getSelectedBearingOnCarriedComponentDesign(
  carriedComponentConnection: JackGirderConnection,
  bearingsOnCarriedComponentDesign: readonly Bearing[],
) {
  return (
    bearingsOnCarriedComponentDesign.find((bearing) => bearing.guid === carriedComponentConnection.jackBearingGuid) ??
    null
  );
}

const getMemberGeometriesForMemberType = (memberType: MemberType, componentDesign: ComponentDesign) => {
  const membersOfType = componentDesign.component.members.filter((member) => member.memberType === memberType);
  return membersOfType.map((member) => calculateMemberPolygon(member, componentDesign.component)).filter(notNullish);
};

const xLocationIsValid = (xLocation: number, memberType: MemberType, componentDesign: ComponentDesign) => {
  const memberGeometries = getMemberGeometriesForMemberType(memberType, componentDesign);
  return memberGeometries.some((memberGeometry) => {
    const memberVertices = memberGeometry.vertices.map((vertex) => vertex.x);
    return xLocation >= Math.min(...memberVertices) && xLocation <= Math.max(...memberVertices);
  });
};

/**
 * @returns The first invalid spaced x location, or null if all spaced locations are valid.
 */
const getFirstInvalidSpacedCarriedComponent = (
  xLocation: number,
  memberType: MemberType,
  numberOfJacks: number,
  spacing: number,
  componentDesign: ComponentDesign,
) => {
  for (let i = 1; i < numberOfJacks; i++) {
    const xLocationForJackAtIndex = xLocation + i * spacing;
    const xLocationIsValidForJackAtIndex = xLocationIsValid(xLocationForJackAtIndex, memberType, componentDesign);
    if (!xLocationIsValidForJackAtIndex) return xLocationForJackAtIndex;
  }
  return null;
};

const areMultipleChoicesForChordLocation = (
  xLocation: number,
  memberType: MemberType,
  componentDesign: ComponentDesign,
) => {
  const memberGeometries = getMemberGeometriesForMemberType(memberType, componentDesign);
  const verticalLineAtXLocation = new Line2D(new Point2D(xLocation, 0), Direction2D.up);
  return (
    memberGeometries.filter((memberGeometry) =>
      memberGeometry.edges.some((memberSide) => memberSide.intersectWithLine(verticalLineAtXLocation)),
    ).length > 1
  );
};

interface CarriedComponentConnectionAndConnectors {
  readonly carriedComponentConnection: JackGirderConnection;
}

interface Props {
  readonly carriedComponentConnection: JackGirderConnection;
  readonly carryingComponentDesign: ComponentDesign;
  readonly carriedComponentDesignCandidates: readonly ComponentDesign[];
  readonly onChange: (carriedComponentConnectionAndConnectors: CarriedComponentConnectionAndConnectors) => void;
  readonly canSubmitWhenInvalid?: boolean;
  // In the editing case where saving is instant, it makes sense to disable this because we cannot gather the additional
  // information needed (e.g. which bearing) before saving. Desiring to make this change while editing seems unlikely as
  // well.
  readonly disableChangingCarriedComponent?: boolean;
  readonly disabled?: boolean;

  readonly layout: 'single-column' | 'two-columns';
}

export function CarriedComponentConnectionProperties(props: Props) {
  const {
    carriedComponentConnection,
    carriedComponentDesignCandidates,
    carryingComponentDesign,
    disableChangingCarriedComponent,
    disabled,
    canSubmitWhenInvalid,
  } = props;

  const dispatch = useAppDispatch();

  const selectedCarriedComponentDesign = getSelectedCarriedComponentDesign(
    carriedComponentConnection,
    carriedComponentDesignCandidates,
  );
  const bearingsOnCarriedComponentDesign = selectedCarriedComponentDesign?.component.bearings ?? [];
  const selectedBearingOnCarriedComponentDesign = getSelectedBearingOnCarriedComponentDesign(
    carriedComponentConnection,
    bearingsOnCarriedComponentDesign,
  );

  function onChangeCarriedComponentConnection(partialCarriedComponentConnection: Partial<JackGirderConnection>) {
    props.onChange({
      carriedComponentConnection: { ...carriedComponentConnection, ...partialCarriedComponentConnection },
    });
  }

  const componentsForAutocomplete = uniqBy(
    carriedComponentDesignCandidates,
    (componentDesign) => componentDesign.component.name,
  );

  const connectingHardware =
    carriedComponentConnection.hangerGuid == null
      ? null
      : throwIfNull(
          carryingComponentDesign.component.hangers.find(({ guid }) => guid === carriedComponentConnection.hangerGuid),
        );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <PropertySelectField
        disabled
        sx={{ marginBottom: 2 }}
        property="Girder Component"
        value={carryingComponentDesign!}
        options={[carryingComponentDesign!]}
        getName={(componentDesign) => componentDesign.component.name}
        getValue={(componentDesign) => componentDesign.guid}
      />

      <Box
        sx={[
          {
            gap: 2,
            '& > *': { margin: '0!important' },
          },

          props.layout === 'single-column'
            ? {
                display: 'flex',
                flexDirection: 'column',
              }
            : {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              },
        ]}
      >
        <Autocomplete
          disablePortal
          disabled={disabled || disableChangingCarriedComponent}
          getOptionLabel={(guid) => {
            const design = componentsForAutocomplete.find((d) => d.guid === guid);
            return design ? design.component.name : '';
          }}
          options={componentsForAutocomplete.map((design) => design.guid)}
          value={selectedCarriedComponentDesign?.guid ?? null}
          onChange={(_, componentDesignGuid) => {
            const componentDesign = componentsForAutocomplete.find((d) => d.guid === componentDesignGuid);
            if (!componentDesignGuid || !componentDesign) return;

            onChangeCarriedComponentConnection({ jackGuid: componentDesignGuid });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Carried Component"
              variant="outlined"
              size="small"
              error={selectedCarriedComponentDesign === null}
            />
          )}
        />

        <PropertySelectField
          property="Bearing on Carried Component"
          value={selectedBearingOnCarriedComponentDesign}
          options={bearingsOnCarriedComponentDesign}
          getName={(bearing) => {
            if (!selectedCarriedComponentDesign) return;
            const bearingVisualData = getBearingVisualData(selectedCarriedComponentDesign.component, bearing);
            if (!bearingVisualData) {
              console.error('Expected bearing visual data to exist.');
              return;
            }
            const { x, y } = bearingVisualData.position;
            return `X: ${formatLengthToArchitecturalString(x)} Y: ${formatLengthToArchitecturalString(y)}`;
          }}
          getValue={(bearing) => bearing.guid}
          submit={(bearing) => onChangeCarriedComponentConnection({ jackBearingGuid: bearing.guid })}
          error={selectedBearingOnCarriedComponentDesign === null}
          disabled={disabled}
        />

        <PropertySelectField
          property="Carrying Member Type"
          value={carriedComponentConnection.memberType}
          options={memberTypeOptions}
          getName={humanCase}
          submit={(memberType) => onChangeCarriedComponentConnection({ memberType })}
          disabled={disabled}
        />

        <DistanceField
          label="X Location"
          value={carriedComponentConnection.xLocation}
          validator={(value) => {
            if (!xLocationIsValid(value, carriedComponentConnection.memberType, carryingComponentDesign)) {
              return 'not on member';
            }
          }}
          onSubmit={(xLocation) => onChangeCarriedComponentConnection({ xLocation })}
          canSubmitWhenInvalid={canSubmitWhenInvalid}
          disabled={disabled}
        />

        <PropertySelectField
          property="Chord to use when multiple choices"
          value={carriedComponentConnection.chordToUseWhenMultipleChoices}
          options={chordToUseWhenMultipleChoicesOptions}
          getName={humanCase}
          submit={(chordToUseWhenMultipleChoices) =>
            onChangeCarriedComponentConnection({ chordToUseWhenMultipleChoices })
          }
          disabled={
            disabled ||
            !areMultipleChoicesForChordLocation(
              carriedComponentConnection.xLocation,
              carriedComponentConnection.memberType,
              carryingComponentDesign,
            )
          }
        />

        <NumericField
          label="Number of Carried Components"
          value={carriedComponentConnection.numberOfJacks}
          onSubmit={(numberOfJacks) => onChangeCarriedComponentConnection({ numberOfJacks })}
          canSubmitWhenInvalid={canSubmitWhenInvalid}
          validator={(numberOfJacks) => {
            const firstLocation = getFirstInvalidSpacedCarriedComponent(
              carriedComponentConnection.xLocation,
              carriedComponentConnection.memberType,
              numberOfJacks,
              carriedComponentConnection.spacingOfJacks,
              carryingComponentDesign,
            );

            if (firstLocation != null) return 'component is off the truss';
          }}
          integers
          min={minimumNumberOfJacks}
          disabled={disabled}
        />

        <DistanceField
          label="Distance Between Repeated Carried Components"
          value={carriedComponentConnection.spacingOfJacks}
          onSubmit={(spacingOfJacks) => onChangeCarriedComponentConnection({ spacingOfJacks })}
          canSubmitWhenInvalid={canSubmitWhenInvalid}
          validator={(value) => {
            const firstLocation = getFirstInvalidSpacedCarriedComponent(
              carriedComponentConnection.xLocation,
              carriedComponentConnection.memberType,
              carriedComponentConnection.numberOfJacks,
              value,
              carryingComponentDesign,
            );

            if (firstLocation != null) {
              return 'not on a member';
            }
          }}
          disabled={disabled || carriedComponentConnection.numberOfJacks <= minimumNumberOfJacks}
        />

        <AngleField
          label="Minimum Angle Between Girder and Carried Component"
          value={carriedComponentConnection.minimumAngleBetweenGirderAndJack}
          onSubmit={(minimumAngleBetweenGirderAndJack) =>
            onChangeCarriedComponentConnection({ minimumAngleBetweenGirderAndJack })
          }
          defaultDisplayFormat="degrees"
          disableSwitchingDisplayFormat
          min={minimumAngleBetweenGirderAndCarriedComponentLowerBound}
          max={minimumAngleBetweenGirderAndCarriedComponentUpperBound}
          disabled={disabled}
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={carriedComponentConnection.locked}
            onChange={(_, checked) => onChangeCarriedComponentConnection({ locked: checked })}
            disabled={disabled}
          />
        }
        label="Lock (safe from regeneration)"
      />

      <PropertiesPanelSubtitle>Connectors</PropertiesPanelSubtitle>

      {connectingHardware ? (
        <Tooltip title="View This Connection">
          <Button variant="outlined" onClick={() => dispatch(selectItem(connectingHardware, 'hanger'))}>
            {connectorDisplayName(connectingHardware.connector)}
          </Button>
        </Tooltip>
      ) : (
        <Typography>- None -</Typography>
      )}
    </Box>
  );
}
